import { company } from '~/company/Company';

export const API_URL: string = import.meta.env.SSR
  ? process.env.REACT_APP_API_ROOT!
  : company.envVariables.REACT_APP_API_ROOT ||
    window.location.origin.replace('frontend-cd-', 'api2-') + '/';

export const REQUEST_TIMEOUT = 30 * 1_000;
export const SSR_REQUEST_TIMEOUT = 2 * 1_000;
export const MAX_REQUEST_ATTEMPT = 4;
export const SSR_MAX_REQUEST_ATTEMPT = 2;

export const ENDPOINT = {
  customer: {
    code: {
      request: 'customer/sms/send',
      verify: 'customer/sms/verify',
    },
    logout: 'customer/auth/logout',
    personalData: {
      request: 'customer/',
      update: 'customer/',
      delete: 'customer/',
    },
    deviceData: {
      create: 'customer/auth/device/create',
      update: 'customer/auth/device/update/',
    },
    subscription: {
      update: 'customer/auth/subscription/update/',
    },
    token: {
      refresh: 'customer/auth/token-refresh',
    },
    address: {
      request: 'customer/address',
      add: 'customer/address',
      update: 'customer/address/',
      delete: 'customer/address/',
    },
    kyc: {
      status: 'customer/v1/kyc/session',
    },
    reviews: {
      requestAll: 'customer/v1/reviews',
      newReview: 'customer/v1/reviews',
      mediaTokens: 'customer/v1/reviews/media/tokens',
    },
    dokobit: {
      check: 'customer/sms/dokobit-status',
      send: 'customer/sms/dokobit-send',
    },
    intercom: {
      identityVerification: 'customer/v1/intercom/identity-verification',
    },
    loyaltyPoints: {
      request: 'customer/v1/loyalty-points',
    },
    loyaltyCard: {
      request: 'customer/v1/loyalty-card',
    },
  },
  bonuses: {
    balance: 'payment/v1/bonuses/balance',
    history: 'payment/v1/bonuses/history',
  },
  loyaltyPoints: {
    balance: 'loyalty-service/v1/wallet/balance',
    history: 'loyalty-service/v1/wallet/transactions',
    terms: 'loyalty-service/v1/configuration/',
  },

  settings: {
    getBackorderLogic: '/orders/v1/settings/backorder_logic',
    getAgeRestriction: '/orders/v1/settings/age_restriction',
  },
  orders: {
    calculate: 'orders/v2/cart/calculate',
    new: 'orders/v2/orders/new',
    boughtItems: 'orders/v1/orders/bought-items',
    paid: 'orders/v1/orders/paid/',
    cancelByUser: 'orders/v1/orders/cancel-by-user',
    status: 'orders/v1/orders',
    getCurrency: 'orders/v1/settings/currency',
    paymentMethods: {
      list: 'payment/v1/payments/methods',
      add: 'payment/v1/payments/methods/add',
      remove: 'payment/v1/payments/methods/remove',
      setupIntent: 'payment/v1/payments/methods/setup-intent',
      setDefault: 'payment/v1/payments/methods/set-default',
      bindCard: 'payment/v1/payments/methods/bind-card',
      bindCardCheckSmsKey:
        'payment/v1/payments/methods/bind-card/check-sms-key',
      status3ds: 'payment/v1/payments/order-id/',
      details: 'payment/v2/payments/details',
      verifone3ds: 'payment/v2/payments/verifone/3ds-token',
      verifone3dsConfirm: 'payment/v2/payments/3ds/confirm',
    },
    feedback: {
      create: 'orders/v1/feedback/create',
      fill: 'orders/v1/feedback/fill/',
    },
    getOrdersWithPromocodes: '/orders/v2/orders/with-promocodes',
  },
  promocodes: {
    get: 'promocodes-service/v1/promocodes/code/',
    getAffiliateCode: 'promocodes-service/v1/promocodes/affiliate/code/',
    getRefereeCode:
      'promocodes-service/v1/promocodes/referral_reward/referee_code/',
    getCustomerRewards:
      'promocodes-service/v1/promocodes/referral_reward/rewards/',
    getReferralInfo:
      '/promocodes-service/v1/promocodes/referral_reward/referee_return_info',
    getRewardsNotifications:
      '/promocodes-service/v1/promocodes/referral_reward/rewards/notify/',
    getRewardStatus:
      '/promocodes-service/v1/promocodes/referral_reward/rewards/status/',
    getPromocodes: '/promocodes-service/v1/customer/{customerId}/promocodes',
  },
  stockSubscriptions: {
    addSubscription: '/customer/v1/stock-subscriptions',
    deleteSubscription: '/customer/v1/stock-subscriptions/unsubscribe',
    getSubscriptions: '/customer/v1/stock-subscriptions/waitlist',
    getSubscriptionsSKU: '/customer/v1/stock-subscriptions/product-sku',
  },
  gifts: {
    getMechanisms: 'orders/v1/gift/gift-mechanisms?warehouse_code=',
  },
  tips: {
    pay: 'delivery/tips/',
    check: 'delivery/tips?order_id=',
  },
  payments: {
    paymentSystemsV2: 'payment/v2/payments/payment-systems',
    paymentSystemsV3: 'payment/v3/payments/payment-systems',
    myfatoorahInitialize: 'payment/v1/payments/myfatoorah/initialize',
  },
  catalog: {
    category: {
      list: 'catalog/v1/client/categories/tree',
    },
    product: {
      listByCategory: 'catalog/v2/tree/categories/',
      listByCategoryPages: '/catalog/v1/client/categories/',
      listByBanner: 'catalog/v1/banner/products',
      request: 'catalog/v1/client/products/',
      bestsellers: 'catalog/v1/bestsellers',
      recommendations: 'catalog/v1/bi/recommendations?warehouse_code=',
      offers: 'catalog/v1/client/offers',
    },
    banner: {
      list: 'catalog/v1/banner',
    },
    search: {
      request: 'catalog/v2/search',
      history: {
        list: 'catalog/v1/search-history',
        delete: 'catalog/v1/search-history/delete',
      },
    },
    filters: {
      list: 'catalog/v1/filters',
    },
    recommendations: {
      list: '/catalog/v2/recommendations',
    },
  },
  deliveryAddress: {
    autocomplete: 'geocoder/v1/api/autocomplete',
    zones: {
      request: 'eta/v2/api/zones',
    },
  },
  delivery: {
    config: {
      /** `{companyId}` is the company UUID (`company.config.id`). */
      isCourierDeliveryEnabled:
        'delivery/v2/api/config/{companyId}/isCourierDeliveryEnabled',
    },
  },
  stories: {
    list: 'stories/v1/stories/byWarehouseCode/',
  },
  eta: {
    get: 'eta/v3/api/eta',
    /** Still v1: the ETA service has no v3 slot endpoint yet. */
    getSlot: 'eta/v1/api/eta-slot',
  },
  favorites: {
    get: 'catalog/v1/favourites',
    addMany: 'catalog/v1/favourites',
    add: 'catalog/v1/favourites/',
    remove: 'catalog/v1/favourites/',
    getIds: 'catalog/v1/favourites/product-ids',
  },
  geocoder: {
    reverseGeocode: 'geocoder/v1/api/reverse-geocode',
  },
  pickupPoints: {
    list: 'wms-connector/v1/pickup-points',
    byId: 'wms-connector/v1/pickup-points/',
    map: 'wms-connector/v2/pickup-points',
  },
  partnerPickupPoints: {
    list: 'delivery/v2/api/pickup-points',
    byId: 'delivery/v2/api/pickup-points/',
  },
  returns: {
    reasons: 'orders/v2/returns/reasons',
    create: 'orders/v2/returns',
  },
};
